import { Box, Container, Typography, Button } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";

const GOLD      = "#D97706";
const GOLD_GRAD = "linear-gradient(135deg,#D97706,#F59E0B)";

const CHECKS = [
  "Unlimited entity screening — no per-hit fees",
  "Onboarding live in under 48 hours",
  "Regulator-ready reports generated in one click",
  "Configurable risk matrices matching your appetite",
  "Role-based access for your entire compliance team",
  "Dedicated compliance expert included with every plan",
  "Sanctions & PEP databases refreshed daily",
  "Full data sovereignty — your region, your control",
];

const PILLARS = [
  { Icon: BoltOutlinedIcon,          accent: GOLD,      title: "Fast to Deploy",   body: "Go live within 48 hours. No hardware. No complex integration. Zero setup friction." },
  { Icon: LockOutlinedIcon,          accent: "#2563EB", title: "Secure by Design", body: "Enterprise-grade encryption, role-based access, and complete audit trails from day one." },
  { Icon: SupportAgentOutlinedIcon,  accent: "#16A34A", title: "Expert Support",   body: "Every account includes a dedicated compliance consultant who knows DNFBP regulations." },
  { Icon: UpdateOutlinedIcon,        accent: "#7C3AED", title: "Always Current",   body: "Sanctions lists update daily. Your team always screens against the latest intelligence." },
];

export default function AdvantagesSection({ onRequestDemo }) {
  return (
    <Box
      id="why-us"
      sx={{ background: "#F8FAFC", py: { xs: 10, md: 16 }, borderTop: "1px solid #E2E8F0" }}
    >
      <Container maxWidth="xl">
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: { xs: 8, lg: 10 },
          alignItems: "center",
        }}>
          {/* Left */}
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: GOLD, letterSpacing: "3px", textTransform: "uppercase", mb: 2, fontFamily: "IBM Plex Sans, sans-serif" }}>
              Why AMLDiligence
            </Typography>
            <Typography component="h2" className="font-display" sx={{ fontSize: { xs: 28, md: 44 }, fontWeight: 700, color: "#0F172A", letterSpacing: "-1.5px", lineHeight: 1.1, mb: 3 }}>
              Built for DNFBPs,
              <br />
              <Box component="span" sx={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                not adapted from banking
              </Box>
            </Typography>
            <Typography sx={{ fontSize: 16, color: "#64748B", lineHeight: 1.85, mb: 5, maxWidth: 480, fontFamily: "IBM Plex Sans, sans-serif" }}>
              Most AML tools were built for banks then retrofitted for precious metals dealers, real estate firms, and accountants. AMLDiligence was designed from scratch for DNFBPs — the workflows, reports, and risk logic match exactly what UAE regulators expect.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75, mb: 5 }}>
              {CHECKS.map(c => (
                <Box key={c} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 17, color: "#16A34A", mt: ".15em", flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 14, color: "#475569", fontFamily: "IBM Plex Sans, sans-serif" }}>{c}</Typography>
                </Box>
              ))}
            </Box>

            <Button
              onClick={onRequestDemo}
              sx={{
                px: 4, py: 1.75, borderRadius: "10px",
                fontSize: 15, fontWeight: 600, textTransform: "none",
                fontFamily: "IBM Plex Sans, sans-serif",
                background: GOLD_GRAD, color: "#fff",
                boxShadow: "0 6px 20px rgba(217,119,6,.3)",
                "&:hover": { background: "linear-gradient(135deg,#B45309,#D97706)", transform: "translateY(-1px)", boxShadow: "0 10px 28px rgba(217,119,6,.4)" },
                transition: "all .22s",
              }}
            >
              Talk to a Compliance Expert
            </Button>
          </Box>

          {/* Right — pillar cards */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
            {PILLARS.map(p => (
              <Box key={p.title} sx={{
                background: "#fff",
                border: "1px solid #E2E8F0",
                borderRadius: "16px",
                p: 3.5,
                boxShadow: "0 1px 4px rgba(15,23,42,.05)",
                transition: "all .25s",
                "&:hover": {
                  borderColor: p.accent,
                  transform: "translateY(-4px)",
                  boxShadow: `0 16px 40px ${p.accent}14`,
                },
              }}>
                <Box sx={{
                  width: 48, height: 48, borderRadius: "12px",
                  background: `${p.accent}10`, border: `1px solid ${p.accent}20`,
                  display: "flex", alignItems: "center", justifyContent: "center", mb: 2.5,
                }}>
                  <p.Icon sx={{ fontSize: 24, color: p.accent }} />
                </Box>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#0F172A", mb: 1, lineHeight: 1.3, fontFamily: "IBM Plex Sans, sans-serif" }}>
                  {p.title}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#64748B", lineHeight: 1.75, fontFamily: "IBM Plex Sans, sans-serif" }}>
                  {p.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
